import {
  AimOutlined,
  ArrowUpOutlined,
  BankOutlined,
  CodeOutlined,
  CodepenOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  FlagOutlined,
  PictureOutlined,
  SettingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { faCircleNodes, faDrone, faServer } from "@fortawesome/pro-regular-svg-icons";
import { lazy } from "react";
import { matchPath } from "react-router";

import AdminSearchPage from "components/admin/AdminSearchPage";
import PermissionGroupPage from "components/admin/PermissionGroupPage";
import { Theme } from "styles/theme";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { ComponentType } from "react";
import type { FeatureFlags } from "@skydio/pbtypes/pbtypes/tools/cloud_api/feature_flags_pb";

// For more information on code splitting, see
// https://www.notion.so/skydio/Codesplitting-Information-and-Tips-a078e8c191074b878974244ddbcb9927
const AlertReportView = lazy(() => import("./alerts/AlertReportView"));
const SummaryReport = lazy(() => import("./reports/SummaryReport"));
const FlightsReport = lazy(() => import("./reports/FlightsReport"));
const ActivityReport = lazy(() => import("./reports/ActivityReport"));
const MediaView = lazy(() => import("./files/MediaView"));
const GraphiQLPage = lazy(() => import("../graphiql/GraphiQLPage"));
const IntegratedFleetView = lazy(() => import("./fleet/IntegratedFleetView"));
const MissionsView = lazy(() => import("./missions/MissionsView"));
const TeleopView = lazy(() => import("./teleop/TeleopRoute"));
const TeleopRedirectView = lazy(() => import("./teleop/TeleopRedirectView"));
const ScansView = lazy(() => import("./scans/ScansView"));
const SettingsView = lazy(() => import("./settings/SettingsView"));
const OnPremAdminView = lazy(() => import("./settings/OnPremAdminView"));
const ReleasesPage = lazy(() => import("../admin/ReleasesPage"));
const DataFilesPage = lazy(() => import("../admin/DataFilesPage"));
const GroupsPage = lazy(() => import("../admin/GroupsPage"));
const PermissionGroupsPage = lazy(() => import("../admin/PermissionGroupsPage"));
const MissionEditorWaypointsPage = lazy(
  () => import("./mission_editor/MissionEditorWaypointsPageWrapper")
);
const MissionEditor3DScanPage = lazy(
  () => import("./mission_editor/MissionEditor3DScanPageWrapper")
);
const SitePage = lazy(() => import("./settings/sites/SitePage"));
const RestProtobufAPIDocumentationPage = lazy(
  () => import("../admin/RestProtobufAPIDocumentationPage")
);
const Scan3DViewer = lazy(() => import("./scans/viewer/Scan3DViewer"));
const InspectPage = lazy(() => import("../pages/inspect_page"));
const MissionsResultsPage = lazy(() => import("../pages/mission_results"));
const VehicleStreamingPage = lazy(() => import("./streaming/livekit/VehicleStreamingPage"));
const VehicleStreamingRedirectPage = lazy(
  () => import("./streaming/livekit/VehicleStreamingRedirectPage")
);
const MarketplaceView = lazy(() => import("./integrations/MarketplaceView"));
const OnPremPortal = lazy(() => import("./onprem/OnPremPortal"));
const MissionTrackAnimationsView = lazy(() => import("./missions/MissionTrackAnimationsView"));

export interface RouteConfig {
  group?: string;
  key: string;
  titleKey?: string;
  linkPath?: string;
  component?: ComponentType;
  icon?: ComponentType;
  faIcon?: IconDefinition;
  adminOnly?: true;
  disabled?: true;
  skydioAdminOnly?: true;
  showForCloudOrganizations: boolean; // If true, show route if the organization is a locally managed cloud organization
  showForRemoteOnpremOrganizations: boolean; // If true, show route if the organization is a remotely managed on-prem organization
  disabledSuperUser?: true; // If true, disable this page for Internal Skydio Super Users
  exact?: boolean;
  // get the theme override for the route, using feature flags as needed.
  themeOverride?: (currentTheme: Theme, flags: FeatureFlags.AsObject) => Theme;
  hideFromMenu?: true;
}

export const main: RouteConfig[] = [
  {
    titleKey: "On-Prem Portal",
    key: "/onprem/portal",
    component: OnPremPortal,
    icon: DownloadOutlined,
    exact: false,
    showForCloudOrganizations: false,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/flight/vehicle-stream/:vehicleId",
    component: VehicleStreamingPage,
    themeOverride: () => Theme.DARK,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/flight/vehicle-stream",
    component: VehicleStreamingRedirectPage,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/flight/live/:vehicleId",
    component: TeleopView,
    themeOverride: () => Theme.DARK,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/flight/live",
    component: TeleopRedirectView,
    themeOverride: () => Theme.DARK,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/missions/:tab",
    titleKey: "Missions",
    linkPath: "/missions",
    component: MissionsView,
    icon: AimOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/media",
    titleKey: "Media",
    component: MediaView,
    icon: PictureOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/scans",
    titleKey: "Scans",
    component: ScansView,
    icon: CodepenOutlined,
    adminOnly: true,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/reports/alerts", // no titleKey to hide in side bar
    component: AlertReportView,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/reports/flights", // no titleKey to hide in side bar
    component: FlightsReport,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/reports/activity/:tab", // no title key to hide in side bar
    component: ActivityReport,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/reports/summary", // no title key to hide in side bar
    component: SummaryReport,
    icon: FileSearchOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/fleet",
    titleKey: "Fleet",
    adminOnly: true,
    component: IntegratedFleetView,
    faIcon: faDrone,
    exact: false,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/scan/:scanId/viewer",
    component: Scan3DViewer,
    exact: false,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/inspect",
    component: InspectPage,
    exact: false,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/mission_results",
    component: MissionsResultsPage,
    exact: false,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/missions/editor/waypoints/:missionTemplateId",
    component: MissionEditorWaypointsPage,
    exact: false,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/missions/editor/3d-scan/:missionTemplateId",
    component: MissionEditor3DScanPage,
    exact: false,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/site/:siteId",
    component: SitePage,
    exact: false,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/mission/:missionId/tracks",
    component: MissionTrackAnimationsView,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
    hideFromMenu: true,
  },
];

export const extra: RouteConfig[] = [
  {
    key: "extra.profile",
    titleKey: "Profile",
    disabled: true,
    adminOnly: true,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "extra.billing",
    titleKey: "Billing",
    disabled: true,
    adminOnly: true,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/settings",
    titleKey: "Settings",
    component: SettingsView,
    icon: SettingOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/onprem_admin",
    titleKey: "On-Prem",
    component: OnPremAdminView,
    faIcon: faServer,
    showForCloudOrganizations: true,
    // This is for REAL On-Prem orgs, not remote ones.
    showForRemoteOnpremOrganizations: false,
  },
  {
    key: "/integrations",
    titleKey: "Integrations",
    component: MarketplaceView,
    faIcon: faCircleNodes,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: false,
  },
  {
    group: "devTools",
    key: "/graphiql",
    titleKey: "GraphiQL",
    skydioAdminOnly: true,
    component: GraphiQLPage,
    icon: CodeOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    group: "devTools",
    key: "/internal_api_docs",
    titleKey: "REST API",
    skydioAdminOnly: true,
    component: RestProtobufAPIDocumentationPage,
    icon: CodeOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/admin_search",
    titleKey: "Admin Search",
    skydioAdminOnly: true,
    component: AdminSearchPage,
    icon: BankOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/groups",
    titleKey: "Feature Groups",
    skydioAdminOnly: true,
    component: GroupsPage,
    icon: FlagOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/permission_groups",
    titleKey: "Permission Groups",
    skydioAdminOnly: true,
    component: PermissionGroupsPage,
    icon: TeamOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/permission_groups/:groupId",
    titleKey: "Permission Group",
    skydioAdminOnly: true,
    component: PermissionGroupPage,
    hideFromMenu: true,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/releases",
    titleKey: "Releases",
    skydioAdminOnly: true,
    component: ReleasesPage,
    icon: ArrowUpOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
  {
    key: "/data_files",
    titleKey: "Data Files",
    skydioAdminOnly: true,
    component: DataFilesPage,
    icon: FileSearchOutlined,
    showForCloudOrganizations: true,
    showForRemoteOnpremOrganizations: true,
  },
];

const orgPathRegex = /\/o\/\w{8}-\w{4}-\w{4}-\w{4}-\w{12}(.+)/;
// Get the main part of a URL location (after the organization id)
const getRouteFromPathname = (pathname: string) => {
  const result = pathname.match(orgPathRegex);
  return result?.[1];
};

export const getMatchedRoute = (pathname: string) => {
  const allRoutes = extra.concat(main);
  const currentRoute = getRouteFromPathname(pathname);
  return allRoutes.find(
    ({ key }) => currentRoute && !!matchPath(currentRoute, { path: key, exact: true })
  );
};

const showRoute = (route: RouteConfig, isRemoteOnpremOrg: boolean) => {
  if (isRemoteOnpremOrg) {
    return route.showForRemoteOnpremOrganizations;
  } else {
    return route.showForCloudOrganizations;
  }
};

export const getMainRoutes = (isRemoteOnpremOrg: boolean) => {
  return main.filter(entry => showRoute(entry, isRemoteOnpremOrg));
};

export const getExtraRoutes = (isRemoteOnpremOrg: boolean) => {
  return extra.filter(entry => showRoute(entry, isRemoteOnpremOrg));
};

export const getAllRoutes = (isRemoteOnpremOrg: boolean) => {
  return getExtraRoutes(isRemoteOnpremOrg).concat(getMainRoutes(isRemoteOnpremOrg));
};

export const defaultRedirectRoute = (baseOrgUrl: string, isRemoteOnpremOrg: boolean) => {
  return isRemoteOnpremOrg ? `${baseOrgUrl}/onprem/portal` : `${baseOrgUrl}/fleet`;
};
